import { auth, db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';

/**
 * Get user's consent settings
 */
export async function getUserConsentSettings(userId) {
  try {
    const consentRef = doc(db, 'consentSettings', userId);
    const consentSnap = await getDoc(consentRef);
    
    if (consentSnap.exists()) {
      return consentSnap.data();
    }
    
    // Return default consent (all ON) if no settings exist
    return {
      consent: {
        pii_public_allowed: true,
        profile_pic_allowed: true,
        artwork_public_allowed: true,
      },
      accountType: 'student',
      parentEmail: null
    };
  } catch (error) {
    console.error('Error fetching consent settings:', error);
    // Return default consent on error
    return {
      consent: {
        pii_public_allowed: true,
        profile_pic_allowed: true,
        artwork_public_allowed: true,
      },
      accountType: 'student',
      parentEmail: null
    };
  }
}

/**
 * Check if user can share artwork publicly
 */
export async function canShareArtworkPublicly(userId) {
  const consentSettings = await getUserConsentSettings(userId);
  return consentSettings.consent?.artwork_public_allowed === true;
}

/**
 * Check if user's name can be displayed publicly
 */
export async function canDisplayNamePublicly(userId) {
  const consentSettings = await getUserConsentSettings(userId);
  return consentSettings.consent?.pii_public_allowed === true;
}

/**
 * Check if user's profile picture can be displayed publicly
 */
export async function canDisplayProfilePicPublicly(userId) {
  const consentSettings = await getUserConsentSettings(userId);
  return consentSettings.consent?.profile_pic_allowed === true;
}

/**
 * Get display name based on consent settings for ANY user (not just current user)
 * Returns nickname if name sharing is off, otherwise returns real name
 * This can be used to display other users' names respecting their consent settings
 */
export async function getDisplayNameForUser(userId) {
  try {
    // First check if user is under 13
    const userDocRef = doc(db, 'users', userId);
    const userDocSnap = await getDoc(userDocRef);
    const isUnder13 = userDocSnap.exists() && !!userDocSnap.data().isUnder13;
    
    // Get profile data - artistProfiles uses uid field, not document ID
    const profileQuery = query(
      collection(db, 'artistProfiles'),
      where('uid', '==', userId)
    );
    const profileSnapshot = await getDocs(profileQuery);
    
    if (!profileSnapshot.empty) {
      const profileData = profileSnapshot.docs[0].data();
      
      // For under-13 users, always use nickname (never real name)
      if (isUnder13) {
        return profileData.nickname || profileData.name || 'Artist';
      }
      
      // For 13+ users, check consent settings
      const consentSettings = await getUserConsentSettings(userId);
      const canDisplayName = consentSettings.consent?.pii_public_allowed === true;
      
      if (canDisplayName) {
        return profileData.name || 'Artist';
      } else {
        // Use nickname if name sharing is off
        return profileData.nickname || profileData.name || 'Artist';
      }
    }
    
    // Fallback
    return 'Artist';
  } catch (error) {
    console.error('Error getting display name for user:', error);
    return 'Artist';
  }
}

/**
 * Get display name based on consent settings
 * Returns nickname if name sharing is off, otherwise returns real name
 * For current user only
 */
export async function getDisplayName(userId) {
  try {
    const user = auth.currentUser;
    if (!user) return 'Artist';
    
    // Use the general function for any user
    return await getDisplayNameForUser(userId);
  } catch (error) {
    console.error('Error getting display name:', error);
    return 'Artist';
  }
}

/**
 * Get profile picture URL or avatar emoji based on user age and consent settings
 * For under-13 users: returns avatar emoji if avatarId exists
 * For 13+ users: returns profile picture URL if consent allows, otherwise null
 */
export async function getProfilePictureUrl(userId) {
  try {
    // Check if user is under 13
    const userDocRef = doc(db, 'users', userId);
    const userDocSnap = await getDoc(userDocRef);
    const isUnder13 = userDocSnap.exists() && !!userDocSnap.data().isUnder13;
    
    // Get profile data
    const profileQuery = query(
      collection(db, 'artistProfiles'),
      where('uid', '==', userId)
    );
    const profileSnapshot = await getDocs(profileQuery);
    
    if (!profileSnapshot.empty) {
      const profileData = profileSnapshot.docs[0].data();
      
      // For under-13 users, return avatar emoji
      if (isUnder13 && profileData.avatarId) {
        const { getAvatarEmoji } = await import('../components/AvatarSelector');
        return getAvatarEmoji(profileData.avatarId);
      }
      
      // For 13+ users, check consent settings
      const consentSettings = await getUserConsentSettings(userId);
      const canDisplayPic = consentSettings.consent?.profile_pic_allowed === true;
      
      if (!canDisplayPic) {
        // Return default avatar
        return null;
      }
      
      return profileData.profilePicture || null;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting profile picture:', error);
    return null;
  }
}

/**
 * Check if user is under 13
 * Checks the isUnder13 flag in the user document
 */
export async function isUnder13(userId) {
  try {
    const userDocRef = doc(db, 'users', userId);
    const userDocSnap = await getDoc(userDocRef);
    if (userDocSnap.exists()) {
      return !!userDocSnap.data().isUnder13;
    }
    // Fallback: check consent settings (old method)
    const consentSettings = await getUserConsentSettings(userId);
    return consentSettings.accountType === 'student' && !!consentSettings.parentEmail;
  } catch (error) {
    console.error('Error checking if user is under 13:', error);
    return false;
  }
}

